/**
 * Tiny wrapper around the three-step R2 upload flow:
 *   1) POST /api/uploads/presign  → signed PUT URL (bound to size+type)
 *   2) PUT bytes directly to R2
 *   3) POST /api/uploads/confirm  → mothership marks the file landed
 *
 * Mothership routes the bytes through SyncSuite Services, so storage is
 * billed to the workspace's wallet and the bucket layout stays consistent
 * across every CRM pod.
 *
 * Used by avatar uploads, email composer image-inserts, and anywhere else
 * we want a public R2-hosted URL instead of base64-bloating the JSON body.
 */
import { api } from "./api";

export interface UploadResult {
  publicUrl: string;
  fileId: string;
  key: string;
}

export interface UploadOptions {
  /** Logical path inside the bucket. e.g. "mail/attachments/img-1234.png". */
  path: string;
  /** "public" for assets that should be openly accessible (avatars, inline
   *  email images); "private" for anything that needs a fresh signed URL
   *  per access. Defaults to "public". */
  bucket?: "public" | "private";
}

const MAX_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB — covers typical inline mail images comfortably

export async function uploadFile(file: File, options: UploadOptions): Promise<UploadResult> {
  if (file.size > MAX_SIZE_BYTES) {
    throw new Error(`File too large (${Math.round(file.size / 1024 / 1024)} MB). Max ${MAX_SIZE_BYTES / 1024 / 1024} MB.`);
  }

  const presign = await api.post<{
    fileId: string;
    uploadUrl: string;
    requiredHeaders: Record<string, string>;
    publicUrl: string;
    key: string;
  }>("/uploads/presign", {
    contentType: file.type || "application/octet-stream",
    sizeBytes: file.size,
    path: options.path,
    bucket: options.bucket ?? "public",
  });

  const put = await fetch(presign.uploadUrl, {
    method: "PUT",
    headers: presign.requiredHeaders ?? { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);

  // Confirm is fire-and-forget — even if it fails the bytes are in R2 and
  // the publicUrl is already valid. Mothership uses confirms for billing
  // reconciliation, not access.
  void api.post("/uploads/confirm", { fileId: presign.fileId }).catch(() => {});

  return {
    publicUrl: presign.publicUrl,
    fileId: presign.fileId,
    key: presign.key,
  };
}

/** Sanitize a filename into a path-safe slug + dedupe with a timestamp. */
export function makeUploadFilename(originalName: string, prefix = "file"): string {
  const ext = originalName.includes(".") ? originalName.slice(originalName.lastIndexOf(".") + 1).toLowerCase() : "bin";
  const safeExt = ext.replace(/[^a-z0-9]/g, "").slice(0, 8) || "bin";
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${safeExt}`;
}
